import { dash } from "@dropbox/api-v2-client";
import { Chip } from "@dropbox/dig-components/chip";
import { UIIcon } from "@dropbox/dig-icons";
import {
  EmojiFrownLine,
  EmojiHeartEyesLine,
  EmojiSmileLine,
  EmojiUneasyLine,
} from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Select_DashSearchResultLabels } from "@mirage/analytics/events/types/select_dash_search_result_labels";
import { showSnackbar } from "@mirage/shared/snackbar";
import { useState } from "react";
import styles from "./ResultAnnotations.module.css";

import type { ChipVariant } from "@dropbox/dig-components/chip";
import { RelevanceRubric } from "@mirage/service-compute-relevance/service/types";
import type { SearchResult } from "@mirage/service-dbx-api";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { SearchFilter } from "@mirage/shared/search/search-filters";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import classNames from "classnames";
import SearchRelevance from "../SearchRelevance";

export enum AnnotationScore {
  Bad = 1,
  Fair = 2,
  Good = 3,
  Perfect = 4,
}

type ResultAnnotationsProps = {
  resultPosition: number;
  result: SearchResult;
  query: string;
  activeFilters?: SearchFilter[];
  base64Image?: string;
  rubric: RelevanceRubric;
  size?: "regular" | "compact";
};

export const feedbackConfig = [
  {
    title: "Perfect",
    defaultIcon: EmojiHeartEyesLine,
    type: AnnotationScore.Perfect,
    tooltipTitle: "Exactly what I was looking for",
  },
  {
    title: "Good",
    defaultIcon: EmojiSmileLine,
    type: AnnotationScore.Good,
    tooltipTitle: "Relevant and should appear in search results or answer",
  },
  {
    title: "Fair",
    defaultIcon: EmojiUneasyLine,
    type: AnnotationScore.Fair,
    tooltipTitle: "Somewhat relevant but not what I was looking for",
  },
  {
    title: "Bad",
    defaultIcon: EmojiFrownLine,
    type: AnnotationScore.Bad,
    tooltipTitle: "Irrelevant and shouldn't appear in search results or answer",
  },
];

export const ResultAnnotations = ({
  resultPosition,
  result,
  query,
  activeFilters,
  base64Image,
  rubric,
  size = "regular",
}: ResultAnnotationsProps) => {
  const { reportPapEvent, searchSessionManager, searchAttemptSessionManager } =
    useMirageAnalyticsContext();
  const { uuid: resultUuid, searchRequestId } = result;
  const [selectedScore, setSelectedScore] = useState<
    AnnotationScore | undefined
  >(undefined);
  const showSourceIcon = convertFeatureValueToBool(
    useFeatureFlagValue("dash_2025_07_22_result_source_icon", false),
  );

  const handleSelect = (feedbackScore: AnnotationScore) => {
    if (feedbackScore === selectedScore) {
      return;
    }

    setSelectedScore(feedbackScore);
    reportPapEvent(
      PAP_Select_DashSearchResultLabels({
        queryString: query,
        resultUuid,
        feedbackScore,
        resultPosition,
        searchRequestId,
        searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
        searchAttemptId: searchAttemptSessionManager.getSessionIdOrUndefined(),
      }),
    );

    showSnackbar({
      title:
        "Annotation submitted! Thanks for helping us improve search results.",
      marginBottom: 50,
    });
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={classNames(styles.container, {
        [styles.compact]: size === "compact",
      })}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {showSourceIcon && <SourceIcon result={result} />}

      <SearchRelevance
        key={result.uuid} // Force re-render and re-fetch of relevance when the result changes
        rubric={rubric}
        query={query}
        activeFilters={activeFilters}
        searchResult={result}
        base64Image={base64Image}
      />
      {feedbackConfig.map((item) => (
        <FeedbackChip
          key={item.type}
          item={item}
          selectedScore={selectedScore}
          handleSelect={handleSelect}
          variant="transparent"
          size={size}
        />
      ))}
    </div>
  );
};

type FeedbackChipProps = {
  item: (typeof feedbackConfig)[0];
  selectedScore: AnnotationScore | undefined;
  handleSelect: (score: AnnotationScore) => void;
  variant?: ChipVariant;
  size?: "regular" | "compact";
};

export function FeedbackChip({
  item,
  selectedScore,
  handleSelect,
  variant = "attention",
  size = "regular",
}: FeedbackChipProps) {
  return (
    <DigTooltip title={item.tooltipTitle}>
      <Chip
        variant={variant}
        onClick={() => {
          handleSelect(item.type);
        }}
        isSelected={selectedScore === item.type}
        selectedStyle="stroke"
        size="small"
        type="button"
      >
        <Chip.IconAccessory>
          <UIIcon size="small" src={item.defaultIcon} />
        </Chip.IconAccessory>
        {size === "regular" && (
          <Chip.Content
            className={classNames(styles.text, {
              [styles.selected]: selectedScore === item.type,
            })}
          >
            {item.title}
          </Chip.Content>
        )}
      </Chip>
    </DigTooltip>
  );
}

// TEMPORARY:
// This is just to help intel folks test/debug during the migration from ES -> OS
// Will remove in a few weeks
const SourceIcon = ({ result }: { result: SearchResult }) => {
  const sourceIndexType = result.sourceIndexType;
  // Get the shortened text and color to show for specific source index type.
  const sourceIconInfo = getSourceIconInfo(sourceIndexType);

  return (
    <DigTooltip
      title={sourceIndexType?.[".tag"] || "Unknown source type"}
      openDelay={400}
      isPortaled={false}
    >
      <div
        className={styles.sourceIcon}
        style={{ backgroundColor: sourceIconInfo.backgroundColor }}
      >
        {sourceIconInfo.text}
      </div>
    </DigTooltip>
  );
};

const getSourceIconInfo = (
  sourceIndexType: dash.SourceIndexType | undefined,
) => {
  const tag = sourceIndexType?.[".tag"];

  // ElasticSearch types: shared and personal
  if (
    tag === "source_index_type_shared" ||
    tag === "source_index_type_personal" ||
    tag === "source_index_type_messages"
  ) {
    return {
      text: "ES", // ElasticSearch
      backgroundColor: "var(--dig-color__accent__cyan)",
    };
  }

  // OpenSearch types: anything ending with "os"
  if (
    tag === "source_index_type_unrecognized_os" ||
    tag === "source_index_type_shared_os" ||
    tag === "source_index_type_personal_os" ||
    tag === "source_index_type_messages_os"
  ) {
    return {
      text: "OS", // OpenSearch
      backgroundColor: "var(--dig-color__accent__green)",
    };
  }

  // Anything else
  return {
    text: "O", // "Other"
    backgroundColor: "var(--dig-color__primary-cta__on-base)",
  };
};
